import { useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Save, X, HandCoins, Receipt } from "lucide-react";
import { http, fmtEUR } from "@/lib/api";
import { toast } from "sonner";

/**
 * «ΣΥΝΕΧΙΣΗ ΕΙΣΠΡΑΞΗΣ» dialog — split a table close between Ταμείο ΑΑ
 * (fiscal, money on the cash register) and Ταμείο ΒΒ (off-register
 * cash for kitchen-only items).
 *
 * Two operating branches:
 *
 *   • fiscal_total > 0 — operator must pick a payment method
 *     (Μετρητά / Κάρτα / …). The chosen amount is committed to ΑΑ via
 *     POST /tables/{id}/continue-payment with {fiscal_payments:[…]}.
 *     Any non_fiscal_total is automatically recorded as a ΒΒ entry by
 *     the backend.
 *
 *   • fiscal_total == 0 — only the floppy «Δισκέτα» button is enabled,
 *     which closes the table without FTEXT and posts the whole
 *     non_fiscal_total into ΒΒ.
 *
 * Props:
 *   open, onOpenChange — standard Radix Dialog pair.
 *   tbl                — the live table object (for name / id).
 *   payments           — list of PaymentMethod for the action buttons.
 *   onSettled          — callback fired after a successful POST; receives
 *                        the refreshed RestaurantTable. The parent uses it
 *                        to refresh table list + close the warning dialog.
 */
export default function ContinuePaymentDialog({ open, onOpenChange, tbl, payments = [], onSettled }) {
  const [preview, setPreview] = useState({ fiscal_total: 0, non_fiscal_total: 0, total: 0 });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [chosenPaymentId, setChosenPaymentId] = useState(null);

  // Load 3-totals preview every time the dialog opens for a new table.
  useEffect(() => {
    if (!open || !tbl?.id) return;
    let cancelled = false;
    setLoading(true);
    http.get(`/tables/${tbl.id}/continue-payment-preview`).then((res) => {
      if (cancelled) return;
      setPreview(res.data || { fiscal_total: 0, non_fiscal_total: 0, total: 0 });
      setLoading(false);
    }).catch(() => {
      if (cancelled) return;
      setLoading(false);
    });
    setChosenPaymentId(null);
    return () => { cancelled = true; };
  }, [open, tbl?.id]);

  const fiscalEnabled = preview.fiscal_total > 0.005;
  const bbEnabled = preview.non_fiscal_total > 0.005 || preview.fiscal_total === 0;

  const submitFiscal = async (paymentId) => {
    if (!fiscalEnabled || busy) return;
    setBusy(true);
    setChosenPaymentId(paymentId);
    try {
      const { data } = await http.post(`/tables/${tbl.id}/continue-payment`, {
        fiscal_payments: [{ payment_id: paymentId, amount: preview.fiscal_total }],
        non_fiscal_only: false,
      });
      toast.success(
        `ΑΑ ${fmtEUR(preview.fiscal_total)}` +
          (preview.non_fiscal_total > 0 ? ` · ΒΒ ${fmtEUR(preview.non_fiscal_total)}` : ""),
      );
      onSettled?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Αποτυχία ΣΥΝΕΧΙΣΗΣ ΕΙΣΠΡΑΞΗΣ");
    } finally {
      setBusy(false);
      setChosenPaymentId(null);
    }
  };

  const submitFloppy = async () => {
    if (busy) return;
    if (preview.fiscal_total > 0.005) {
      toast.error("Δεν επιτρέπεται μόνο ΒΒ όταν υπάρχουν φορολογικά είδη");
      return;
    }
    setBusy(true);
    try {
      const { data } = await http.post(`/tables/${tbl.id}/continue-payment`, {
        fiscal_payments: [],
        non_fiscal_only: true,
      });
      toast.success(`Καταχωρήθηκε στο ΒΒ: ${fmtEUR(preview.non_fiscal_total)}`);
      onSettled?.(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Αποτυχία αποθήκευσης ΒΒ");
    } finally {
      setBusy(false);
    }
  };

  const cashFirst = useMemo(
    () => [...(payments || [])].sort((a, b) => {
      // Push «ΜΕΤΡΗΤΑ» first so the most common touch is the easiest target.
      const ac = /ΜΕΤΡΗΤ/i.test(a.name || "") || a.payment_id === 1;
      const bc = /ΜΕΤΡΗΤ/i.test(b.name || "") || b.payment_id === 1;
      return Number(bc) - Number(ac);
    }),
    [payments],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-2xl w-[95vw] p-0 overflow-hidden"
        data-testid="continue-payment-dialog"
      >
        <DialogHeader className="px-6 pt-5 pb-3 bg-gradient-to-br from-indigo-50 to-blue-50 border-b-2 border-indigo-200">
          <DialogTitle className="text-xl font-bold flex items-center gap-2 text-indigo-900">
            <HandCoins size={22} /> ΣΥΝΕΧΙΣΗ ΕΙΣΠΡΑΞΗΣ — {tbl?.name}
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-700">
            Επίλεξε τρόπο πληρωμής για το <b>φορολογικό</b> μέρος (πάει στο
            ΤΑΜΕΙΟ ΑΑ). Τα <b>μη φορολογικά</b> κλείνουν αυτόματα στο ΤΑΜΕΙΟ ΒΒ.
          </DialogDescription>
        </DialogHeader>

        {/* 3-totals header */}
        <div className="px-6 py-4 grid grid-cols-3 gap-3 bg-white border-b border-slate-200">
          <div
            className="rounded-lg border-2 border-slate-300 bg-slate-50 px-3 py-2 text-center"
            data-testid="cp-total-all"
          >
            <div className="text-[10px] uppercase tracking-wider text-slate-500 font-bold">
              Συνολικό
            </div>
            <div className="text-2xl font-bold font-mono text-slate-800 mt-1">
              {fmtEUR(preview.total)}
            </div>
          </div>
          <div
            className="rounded-lg border-2 border-emerald-300 bg-emerald-50 px-3 py-2 text-center"
            data-testid="cp-total-aa"
          >
            <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold flex items-center justify-center gap-1">
              <Receipt size={11} /> Ταμείο ΑΑ (φορολογικά)
            </div>
            <div className="text-2xl font-bold font-mono text-emerald-700 mt-1">
              {fmtEUR(preview.fiscal_total)}
            </div>
          </div>
          <div
            className="rounded-lg border-2 border-amber-300 bg-amber-50 px-3 py-2 text-center"
            data-testid="cp-total-bb"
          >
            <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold">
              Ταμείο ΒΒ (απλές παρ.)
            </div>
            <div className="text-2xl font-bold font-mono text-amber-700 mt-1">
              {fmtEUR(preview.non_fiscal_total)}
            </div>
          </div>
        </div>

        {/* Payment methods grid */}
        <div className="px-6 py-5">
          <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">
            Τρόπος πληρωμής για ΑΑ
          </div>
          <div className="grid grid-cols-2 gap-3">
            {cashFirst.map((p) => (
              <Button
                key={p.id || p.payment_id}
                onClick={() => submitFiscal(p.payment_id)}
                disabled={!fiscalEnabled || busy || loading}
                className="h-16 font-bold text-base border-2 bg-white hover:bg-emerald-50 hover:border-emerald-400 text-slate-900 border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid={`cp-pay-${p.payment_id}`}
              >
                {chosenPaymentId === p.payment_id && busy ? "Στέλνεται…" : p.name}
              </Button>
            ))}
          </div>

          {/* Διαχωριστικό + Δισκέτα BB save */}
          <div className="mt-5 pt-4 border-t-2 border-dashed border-slate-300">
            <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-2">
              {preview.fiscal_total > 0.005
                ? "Μόνο ΒΒ — διαθέσιμο αν δεν υπάρχουν φορολογικά"
                : "Καταχώρηση στο Ταμείο ΒΒ χωρίς εκτύπωση"}
            </div>
            <Button
              onClick={submitFloppy}
              disabled={!bbEnabled || busy || loading || preview.fiscal_total > 0.005}
              className="w-full h-14 bg-amber-500 hover:bg-amber-600 text-white font-bold text-base disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="cp-floppy-save"
            >
              <Save size={18} className="mr-2" />
              Δισκέτα — Αποθήκευση {fmtEUR(preview.non_fiscal_total)} στο ΒΒ
            </Button>
          </div>

          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={busy}
            className="w-full mt-4 h-11 font-bold border-2"
            data-testid="cp-cancel"
          >
            <X size={16} className="mr-1.5" /> Ακύρωση
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
